import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
  type RefObject,
} from "react";
import { HermesDrawer } from "@/components/hermes/HermesDrawer";
import { CustomerChatDock } from "@/components/journey/CustomerChatDock";
import { CustomerChatProvider, useCustomerChat } from "@/components/journey/CustomerChatProvider";
import { CustomerChatWindow } from "@/components/journey/CustomerChatWindow";
import { HermesProvider, useHermes } from "@/components/hermes/HermesProvider";
import { MaterialsUploadProgress } from "@/components/materials/MaterialsUploadProgress";
import { BackgroundTasksPanel } from "@/components/shell/BackgroundTasksPanel";
import { BackgroundTasksProvider } from "@/lib/background-tasks";
import { AppSidebar } from "./AppSidebar";
import { CreateAccountDialog } from "./CreateAccountDialog";
import { CreateBrandDialog } from "./CreateBrandDialog";
import { SiteHeader } from "./SiteHeader";
import { IMPERSONATION_BANNER_OFFSET_PX } from "@/components/platform/ImpersonationBanner";
import { SidebarProvider } from "@/components/ui/sidebar";
import { useAccountWorkspace } from "@/lib/account-workspace";
import { useAuth } from "@/lib/auth";
import { MaterialsUploadProvider } from "@/lib/materials-upload-queue";
import { cn } from "@/lib/utils";

const HERMES_WIDTH_KEY = "hermes-workspace:width";
/** Open/close: Hermes column width 0 ↔ target; main flex-shrinks. */
const HERMES_DOCK_MS = 280;
const HERMES_DEFAULT_W = 420;
const HERMES_MIN_W = 320;
const HERMES_MAX_RATIO = 0.55;
const HERMES_GAP_PX = 4;
/** 全宽浮层与壳层 md:m-2 对齐的视口内边距 */
const HERMES_OVERLAY_INSET_PX = 8;

/**
 * 全宽展开：独立 fixed 浮层盖住侧栏 + 主区，下方 flex 布局用占位保留原尺寸（不挤压、不重排）。
 * 侧栏抽屉态：仍在内容行内用宽度动画开合。
 */

/** Shared inset card chrome — main and Hermes are separate regions. */
const PANE_CARD_CLASS =
  "flex h-full min-h-0 min-w-0 flex-col overflow-hidden bg-background md:rounded-lg md:border md:border-border/60 md:shadow-sm";

type OverlayBox = { top: number; right: number; bottom: number; left: number };

function shellOverlayBox(bannerOffsetPx = 0): OverlayBox {
  return {
    top: HERMES_OVERLAY_INSET_PX + bannerOffsetPx,
    right: HERMES_OVERLAY_INSET_PX,
    bottom: HERMES_OVERLAY_INSET_PX,
    left: HERMES_OVERLAY_INSET_PX,
  };
}

function rectToOverlayBox(rect: DOMRect): OverlayBox {
  return {
    top: Math.round(rect.top),
    right: Math.round(window.innerWidth - rect.right),
    bottom: Math.round(window.innerHeight - rect.bottom),
    left: Math.round(rect.left),
  };
}

function readStoredHermesWidth(): number {
  if (typeof window === "undefined") return HERMES_DEFAULT_W;
  try {
    const raw = window.localStorage.getItem(HERMES_WIDTH_KEY);
    const n = raw ? Number(raw) : NaN;
    if (Number.isFinite(n) && n >= HERMES_MIN_W) return Math.round(n);
  } catch {
    /* ignore */
  }
  return HERMES_DEFAULT_W;
}

function persistHermesWidth(width: number) {
  try {
    window.localStorage.setItem(HERMES_WIDTH_KEY, String(Math.round(width)));
  } catch {
    /* ignore */
  }
}

function clampHermesWidth(width: number, rowWidth: number): number {
  const max = Math.max(HERMES_MIN_W, Math.floor(rowWidth * HERMES_MAX_RATIO));
  return Math.min(max, Math.max(HERMES_MIN_W, Math.round(width)));
}

/**
 * Drag handle between main and Hermes (pixel widths; compatible with CSS open/close).
 * Visual language mirrors ResizableHandle (thin grip).
 */
function HermesResizeHandle({
  width,
  rowRef,
  onResize,
  disabled,
}: {
  width: number;
  rowRef: RefObject<HTMLDivElement | null>;
  onResize: (nextWidth: number) => void;
  disabled?: boolean;
}) {
  const draggingRef = useRef(false);
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);
  const widthRef = useRef(width);
  widthRef.current = width;

  const onPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      if (disabled || event.button !== 0) return;
      event.preventDefault();
      event.stopPropagation();
      draggingRef.current = true;
      startXRef.current = event.clientX;
      startWidthRef.current = widthRef.current;
      event.currentTarget.setPointerCapture(event.pointerId);
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
    },
    [disabled],
  );

  const onPointerMove = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      if (!draggingRef.current) return;
      event.preventDefault();
      const rowWidth = rowRef.current?.getBoundingClientRect().width ?? 0;
      if (rowWidth <= 0) return;
      // Hermes is on the right: drag left → wider, drag right → narrower.
      const delta = startXRef.current - event.clientX;
      const next = clampHermesWidth(startWidthRef.current + delta, rowWidth);
      widthRef.current = next;
      onResize(next);
    },
    [onResize, rowRef],
  );

  const endDrag = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    try {
      event.currentTarget.releasePointerCapture(event.pointerId);
    } catch {
      /* ignore */
    }
    document.body.style.cursor = "";
    document.body.style.userSelect = "";
    persistHermesWidth(widthRef.current);
  }, []);

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label="调整 Hermes 宽度"
      aria-disabled={disabled || undefined}
      tabIndex={disabled ? -1 : 0}
      className={cn(
        "relative z-10 flex h-full w-full touch-none cursor-col-resize items-center justify-center",
        "bg-transparent outline-none",
        "focus-visible:ring-1 focus-visible:ring-ring",
        disabled && "pointer-events-none",
      )}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
    >
      <div className="z-10 flex h-6 w-1 shrink-0 rounded-lg bg-border" />
    </div>
  );
}

function WorkspaceChrome({ children }: { children: ReactNode }) {
  const {
    createAccountOpen,
    setCreateAccountOpen,
    createBrandOpen,
    setCreateBrandOpen,
  } = useAccountWorkspace();
  const { impersonation } = useAuth();
  const bannerOffsetPx = impersonation ? IMPERSONATION_BANNER_OFFSET_PX : 0;
  const { open, expanded, brandSiteStudioActive } = useHermes();
  const { isFullscreen } = useCustomerChat();
  const hermesDockAllowed = open && !brandSiteStudioActive && !isFullscreen;
  const viewportOverlayBox = useCallback(
    () => shellOverlayBox(bannerOffsetPx),
    [bannerOffsetPx],
  );

  /** Preferred dock width when not expanded (drag / localStorage). */
  const [hermesWidth, setHermesWidth] = useState(readStoredHermesWidth);
  /** Visual column width in px when docked (not in fixed overlay mode). */
  const [columnWidth, setColumnWidth] = useState(open ? hermesWidth : 0);
  const [dockMounted, setDockMounted] = useState(open);
  const [transitioning, setTransitioning] = useState(false);
  /** fixed 浮层：盖住侧栏 + 主区，下方用占位保住 dock 足迹。 */
  const [expandOverlay, setExpandOverlay] = useState(false);
  /** fixed 浮层的 inset（相对视口）；null 表示用 columnWidth 抽屉态。 */
  const [overlayBox, setOverlayBox] = useState<OverlayBox | null>(null);
  const prevOpenRef = useRef(open);
  const prevExpandedRef = useRef(expanded);
  const contentWidthRef = useRef(hermesWidth);
  const expandOverlayRef = useRef(expandOverlay);
  const overlayBoxRef = useRef<OverlayBox | null>(overlayBox);
  const rowRef = useRef<HTMLDivElement>(null);
  const hermesPaneRef = useRef<HTMLDivElement>(null);
  /** 全宽时保留抽屉足迹的占位，用于塌回测量 */
  const dockSlotRef = useRef<HTMLDivElement>(null);
  expandOverlayRef.current = expandOverlay;
  overlayBoxRef.current = overlayBox;

  const resolveDockWidth = useCallback(() => contentWidthRef.current, []);

  const measureDockSlotBox = useCallback((): OverlayBox => {
    const slot = dockSlotRef.current?.getBoundingClientRect();
    if (slot && slot.width > 0) return rectToOverlayBox(slot);
    const row = rowRef.current?.getBoundingClientRect();
    const dock = contentWidthRef.current;
    if (row && row.width > 0) {
      return {
        top: Math.round(row.top),
        right: Math.round(window.innerWidth - row.right),
        bottom: Math.round(window.innerHeight - row.bottom),
        left: Math.round(row.right - dock),
      };
    }
    return viewportOverlayBox();
  }, [viewportOverlayBox]);

  // Guard on `open` flip only (expanded is read at open time for re-open-expanded).
  // hermesWidth stays in contentWidthRef so drag updates do not restart dock animation.
  useEffect(() => {
    if (prevOpenRef.current === open) return;
    prevOpenRef.current = open;

    if (open) {
      setDockMounted(true);
      const openExpanded = expanded;
      setTransitioning(true);
      if (openExpanded) {
        // 以全宽浮层打开：从视口右侧滑入铺满壳层 inset。
        setExpandOverlay(true);
        setColumnWidth(resolveDockWidth());
        setOverlayBox({
          ...viewportOverlayBox(),
          left: window.innerWidth,
        });
        let cancelled = false;
        const frame = requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (!cancelled) setOverlayBox(viewportOverlayBox());
          });
        });
        const timer = window.setTimeout(() => {
          if (!cancelled) setTransitioning(false);
        }, HERMES_DOCK_MS);
        return () => {
          cancelled = true;
          cancelAnimationFrame(frame);
          window.clearTimeout(timer);
        };
      }

      setExpandOverlay(false);
      setOverlayBox(null);
      setColumnWidth(0);
      let cancelled = false;
      const frame = requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (!cancelled) setColumnWidth(resolveDockWidth());
        });
      });
      const timer = window.setTimeout(() => {
        if (!cancelled) setTransitioning(false);
      }, HERMES_DOCK_MS);
      return () => {
        cancelled = true;
        cancelAnimationFrame(frame);
        window.clearTimeout(timer);
      };
    }

    // 关闭：全宽浮层直接卸掉（不播放「退出全宽→抽屉」morph）；抽屉态收宽度。
    let cancelled = false;
    if (expandOverlayRef.current) {
      setTransitioning(false);
      setExpandOverlay(false);
      setOverlayBox(null);
      setColumnWidth(0);
      setDockMounted(false);
      return;
    }

    const startWidth =
      hermesPaneRef.current?.getBoundingClientRect().width || contentWidthRef.current;
    setOverlayBox(null);
    setColumnWidth(Math.round(startWidth));
    setTransitioning(false);
    const frame = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (cancelled) return;
        setTransitioning(true);
        setColumnWidth(0);
      });
    });
    const timer = window.setTimeout(() => {
      if (!cancelled) {
        setDockMounted(false);
        setExpandOverlay(false);
        setOverlayBox(null);
        setTransitioning(false);
      }
    }, HERMES_DOCK_MS + 32);
    return () => {
      cancelled = true;
      cancelAnimationFrame(frame);
      window.clearTimeout(timer);
    };
  }, [open, expanded, resolveDockWidth, measureDockSlotBox, viewportOverlayBox]);

  // 全宽 ↔ 抽屉：fixed 浮层动画；下方用占位保住布局。
  useEffect(() => {
    if (prevExpandedRef.current === expanded) return;
    prevExpandedRef.current = expanded;
    if (!open || !dockMounted) return;

    let cancelled = false;

    if (expanded) {
      const from =
        hermesPaneRef.current != null
          ? rectToOverlayBox(hermesPaneRef.current.getBoundingClientRect())
          : measureDockSlotBox();
      setExpandOverlay(true);
      setColumnWidth(contentWidthRef.current);
      setOverlayBox(from);
      setTransitioning(true);
      const frame = requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (!cancelled) setOverlayBox(viewportOverlayBox());
        });
      });
      const timer = window.setTimeout(() => {
        if (!cancelled) setTransitioning(false);
      }, HERMES_DOCK_MS);
      return () => {
        cancelled = true;
        cancelAnimationFrame(frame);
        window.clearTimeout(timer);
      };
    }

    // 退出全宽：从当前浮层 inset 收到抽屉足迹，再回到 in-flow。
    const target = measureDockSlotBox();
    setExpandOverlay(true);
    setOverlayBox(overlayBoxRef.current ?? viewportOverlayBox());
    setTransitioning(true);
    const frame = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!cancelled) setOverlayBox(target);
      });
    });
    const timer = window.setTimeout(() => {
      if (!cancelled) {
        setExpandOverlay(false);
        setOverlayBox(null);
        setColumnWidth(contentWidthRef.current);
        setTransitioning(false);
      }
    }, HERMES_DOCK_MS + 32);
    return () => {
      cancelled = true;
      cancelAnimationFrame(frame);
      window.clearTimeout(timer);
    };
  }, [expanded, open, dockMounted, measureDockSlotBox, viewportOverlayBox]);

  const onHermesResize = useCallback((next: number) => {
    contentWidthRef.current = next;
    setHermesWidth(next);
    setColumnWidth(next);
  }, []);

  const showGap = hermesDockAllowed && dockMounted && columnWidth > 0;
  const resizeEnabled = hermesDockAllowed && dockMounted && !transitioning && !expandOverlay;
  const paneWidth = hermesWidth;

  return (
    <SidebarProvider className="h-full max-h-full min-h-0 overflow-hidden">
      <AppSidebar
        style={
          bannerOffsetPx > 0
            ? {
                top: bannerOffsetPx,
                bottom: "auto",
                height: `calc(100svh - ${bannerOffsetPx}px)`,
              }
            : undefined
        }
      />

      {/*
        Three regions: sidebar (peer) | main card | 4px gap | hermes card.
        Outer row uses inset margins so the gap shows chrome (bg-sidebar), not card fill.
        全宽：Hermes 为 fixed 浮层盖住侧栏+主区；行内占位保持原抽屉足迹。
      */}
      <div
        ref={rowRef}
        className={cn(
          "relative flex min-h-0 min-w-0 flex-1 overflow-hidden",
          "md:my-2 md:mr-2 md:ml-0 md:peer-data-[state=collapsed]:ml-2",
        )}
      >
        <main
          data-slot="sidebar-inset"
          className={cn(PANE_CARD_CLASS, "min-w-0 flex-1")}
        >
          <SiteHeader />
          {/*
            主区高度链：外层锁死剩余高度；滚动在 @container 层。
            内层 min-h-full：文档页可随内容长高并滚动；全高工作台页用 flex-1 min-h-0
            填满主区（相对 main，而非 100vh 估算），避免顶出外壳滚动条。
          */}
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            <div className="@container/main flex min-h-0 flex-1 flex-col overflow-y-auto [scrollbar-gutter:stable]">
              <div className="flex min-h-full flex-1 flex-col gap-4 p-4 md:gap-6 md:p-6">
                {children}
              </div>
            </div>
          </div>
        </main>

        {dockMounted && hermesDockAllowed ? (
          <>
            {/* Gap + resize — 抽屉态可拖；全宽时仍保留 4px 足迹以免主区变宽 */}
            <div
              className={cn(
                "flex h-full shrink-0 justify-center",
                transitioning && !expandOverlay && "transition-[width] ease-out",
              )}
              style={{
                width: showGap || expandOverlay ? HERMES_GAP_PX : 0,
                ...(transitioning && !expandOverlay
                  ? { transitionDuration: `${HERMES_DOCK_MS}ms` }
                  : null),
              }}
            >
              {showGap && !expandOverlay ? (
                <HermesResizeHandle
                  width={hermesWidth}
                  rowRef={rowRef}
                  onResize={onHermesResize}
                  disabled={!resizeEnabled}
                />
              ) : null}
            </div>

            {/* 全宽时占位：保住抽屉列宽，主区不被撑开 */}
            {expandOverlay ? (
              <div
                ref={dockSlotRef}
                aria-hidden
                className="h-full shrink-0"
                style={{ width: hermesWidth }}
              />
            ) : null}

            <div
              ref={hermesPaneRef}
              className={cn(
                expandOverlay
                  ? "fixed z-30 overflow-hidden"
                  : "flex h-full shrink-0 justify-end overflow-hidden",
                transitioning &&
                  (expandOverlay
                    ? "transition-[top,right,bottom,left] ease-out"
                    : "transition-[width] ease-out"),
              )}
              style={
                expandOverlay && overlayBox
                  ? {
                      top: overlayBox.top,
                      right: overlayBox.right,
                      bottom: overlayBox.bottom,
                      left: overlayBox.left,
                      ...(transitioning
                        ? { transitionDuration: `${HERMES_DOCK_MS}ms` }
                        : null),
                    }
                  : {
                      width: columnWidth,
                      ...(transitioning
                        ? { transitionDuration: `${HERMES_DOCK_MS}ms` }
                        : null),
                    }
              }
              aria-hidden={!open || (!expandOverlay && columnWidth <= 0)}
            >
              <div
                className={cn(PANE_CARD_CLASS, "h-full")}
                style={
                  expandOverlay
                    ? { width: "100%", minWidth: "100%", height: "100%" }
                    : { width: paneWidth, minWidth: paneWidth }
                }
              >
                <HermesDrawer />
              </div>
            </div>
          </>
        ) : null}
      </div>

      <CreateAccountDialog open={createAccountOpen} onOpenChange={setCreateAccountOpen} />
      <CreateBrandDialog open={createBrandOpen} onOpenChange={setCreateBrandOpen} />
      <div
        className="pointer-events-none fixed right-3 z-50 flex w-[min(100vw-1.5rem,20rem)] flex-col gap-2"
        style={{ top: `calc(0.75rem + ${bannerOffsetPx}px)` }}
      >
        <div className="pointer-events-auto">
          <BackgroundTasksPanel />
        </div>
        <div className="pointer-events-auto">
          <MaterialsUploadProgress />
        </div>
      </div>
      <CustomerChatWindow />
      <CustomerChatDock />
    </SidebarProvider>
  );
}

export function WorkspaceShell({ children }: { children: ReactNode }) {
  return (
    <HermesProvider>
      <CustomerChatProvider>
        <MaterialsUploadProvider>
          <BackgroundTasksProvider>
            <WorkspaceChrome>{children}</WorkspaceChrome>
          </BackgroundTasksProvider>
        </MaterialsUploadProvider>
      </CustomerChatProvider>
    </HermesProvider>
  );
}
